import React, { useMemo, useState } from 'react';
import { Layout } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import { useSearch } from '@okapi/router-react';
import {
  Button,
  GalleryCatalog,
  GalleryCardContent,
  KitchenDownloadCatalog,
  RootLayout,
  SectionTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
  StickyPanel,
} from 'common/components';
import {
  COLLECTIONS_STORE_NAME,
  FacetValue,
  FETCH_COLLECTIONS,
  KitchenCollection,
  selectAvailableCollections,
  selectStyleFacet,
} from 'modules/collections';
import { MODEL_JSON_STORE_NAME, selectRegionId } from 'modules/model-json';
import { State } from 'store/types';
import { analyticsUtils } from 'utils/analytics-utils';
import { DescriptionBlock, InfoBlock } from './components';
import {
  IMAGE_INDENT,
  IMAGE_PER_PAGE,
  KITCHEN_STYLES_ELEMENT_BLOCK,
  KITCHEN_STYLES_PATH,
  DESIGN_PROJECT_BLOCK,
  ONLINE_CALCULATOR_BLOCK,
} from './kitchen-styles-constants';
import {
  button,
  buttonWrapper,
  calculatorInfoBlockStylesMap,
  descriptionInfoBlockStylesMap,
  fixedTabListWrapper,
  header,
  kitchenStylesNeo,
  tabListWrapper,
} from './kitchen-styles-styles';

const INITIAL_PRODUCTS_PER_PAGE = 11;
const PRODUCTS_PER_PAGE = 14;

export const getSelectionGroupList = (styles: FacetValue[]): SelectionGroupOption[] =>
  styles.map(({ name, value }) => ({
    text: value as string,
    value: name,
  }));

/**
 * Вью страницы "Кухни в разных стилях"
 */
export const KitchenStyles: React.FC = () => {
  const [search, setSearchParam] = useSearch();
  const { collections, dispatch } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);
  const regionId = React.useMemo(() => selectRegionId(modelJson), [modelJson]);
  const collectionList = selectAvailableCollections(collections);
  const styles = selectStyleFacet(collections);
  const selectionGroupList = getSelectionGroupList(styles?.values || []);
  const { offset, productsCount } = collections;

  const collectionLength = collectionList.length;

  const activeStyle = useMemo(() => {
    const style = styles?.values?.find(({ name }) => name === search['style-name']);

    return style?.name || 'all';
  }, [styles, search]);

  const [activeSelectionGroup, setActiveSelectionGroup] = useState<string>(activeStyle);
  const [imagePerPage, setImagePerPage] = useState<number>(IMAGE_PER_PAGE);

  /**
   * Обработчик изменения табов со стилями
   */
  const handleTabChange = (e: React.MouseEvent, value: SelectionGroupValue) => {
    if (value !== activeSelectionGroup) {
      setSearchParam({ 'style-name': value as string });
      setImagePerPage(IMAGE_PER_PAGE);
      setActiveSelectionGroup(value as string);

      dispatch(FETCH_COLLECTIONS.clear);
      dispatch(FETCH_COLLECTIONS.init, {
        url: 'get-kitchen-price',
        offset: 0,
        variantId: value,
        landingType: 'new-kitchen',
      });

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      const activeOption: SelectionGroupOption | undefined = selectionGroupList.find(
        (option: SelectionGroupOption) => option.value === value
      );
      if (activeOption) {
        analyticsUtils.clickButtonEvent(
          KITCHEN_STYLES_ELEMENT_BLOCK,
          activeOption.text,
          'Кухни в разных стилях'
        );
      }
    }
  };

  const handleClickShowMoreBtn = () => {
    setImagePerPage(imagePerPage + IMAGE_PER_PAGE + IMAGE_INDENT);
    dispatch(FETCH_COLLECTIONS.init, {
      url: 'get-kitchen-price',
      limit: PRODUCTS_PER_PAGE,
      offset: offset === 0 ? INITIAL_PRODUCTS_PER_PAGE : offset + PRODUCTS_PER_PAGE,
      variantId: activeStyle,
      landingType: 'new-kitchen',
    });
    analyticsUtils.clickButtonEvent(KITCHEN_STYLES_ELEMENT_BLOCK, 'Показать ещё', 'Стили проектных кухонь');
  };

  const handleCardClick = (collection: KitchenCollection, position: number) => {
    analyticsUtils.sendEvent('GA:clickProduct', {
      elementType: 'product',
      elementBlock: `${KITCHEN_STYLES_ELEMENT_BLOCK} / Catalog`,
      elementName: 'productNameLink',
      division: 'Кухни',
      subdivision: 'Проект кухни как пример',
      productName: collection.displayedName,
      productId: collection.productId,
      productPrice: collection.price,
      productPosition: position + 1,
      abTestPrice: null,
      productPrevPrice: null,
      productTag: undefined,
    });
  };

  const handleCollectionPreviewClick = (ckickedCollection: KitchenCollection) => {
    analyticsUtils.clickButtonEvent(`${KITCHEN_STYLES_ELEMENT_BLOCK} / Catalog`, ckickedCollection.displayedName, 'Цвет фасада');
  };

  return (
    <RootLayout currentPage={KITCHEN_STYLES_PATH}>
      <Layout>
        <SectionTitle className={header} as="h1" variant="display">
          Кухни в&nbsp;разных стилях
        </SectionTitle>

        <StickyPanel
          className={tabListWrapper}
          fixedClassName={fixedTabListWrapper}
          renderContent={() => (
            <SelectionGroup
              active={activeSelectionGroup}
              onChange={handleTabChange}
              optionList={selectionGroupList}
              rounded
            />
          )}
        />

        {activeStyle !== 'all' && <DescriptionBlock style={activeStyle} />}

        {collectionList.length > 0 && (
          <GalleryCatalog
            className={activeSelectionGroup === 'neo' ? kitchenStylesNeo : ''}
            catalog={collectionList as KitchenCollection[]}
            onCardClick={handleCardClick}
            onCollectionPreviewClick={handleCollectionPreviewClick}
            renderCardContent={({ displayedName, price }) => <GalleryCardContent displayedName={displayedName} price={price.price} />}
            downloadCatalog={
              <KitchenDownloadCatalog
                elementBlock={KITCHEN_STYLES_ELEMENT_BLOCK}
                elementName="Кухни в разных стилях"
                isNewKitchen
              />
            }
          >
            <InfoBlock
              commonClassName={descriptionInfoBlockStylesMap[collectionLength]}
              block={DESIGN_PROJECT_BLOCK}
              regionId={regionId}
            />
            <InfoBlock
              commonClassName={calculatorInfoBlockStylesMap[collectionLength]}
              block={ONLINE_CALCULATOR_BLOCK}
              regionId={regionId}
            />
          </GalleryCatalog>
        )}

        {PRODUCTS_PER_PAGE + offset <= productsCount && (
          <div className={buttonWrapper}>
            <Button rounded onClick={handleClickShowMoreBtn} adaptive={false} className={button}>
              Показать ещё
            </Button>
          </div>
        )}
      </Layout>
    </RootLayout>
  );
};
